import React, { useEffect, useState, useCallback } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiCalendar,
  FiFilter, FiLogIn, FiLogOut, FiZap, FiEye,
  FiDownload, FiFileText, FiGrid, FiRefreshCw, FiUsers, FiSun
} from 'react-icons/fi';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { exportExcel, exportPDF, exportExcelByUser, exportPDFByUser } from '../utils/attendanceExport';
import './Attendance.css';
import './Holidays.css';

const SERVER_ORIGIN = (process.env.NODE_ENV === 'production'
  ? 'https://creativeglassworld.com'
  : 'http://localhost:9119');

/* ── Holiday constants (moved from Holidays page) ── */
const TYPE_STYLE = {
  national: { label: 'National',  bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', emoji: '🇮🇳' },
  regional: { label: 'Regional',  bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', emoji: '🏛️' },
  company:  { label: 'Company',   bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff', emoji: '🏢' },
  optional: { label: 'Optional',  bg: '#fffbeb', color: '#d97706', border: '#fde68a', emoji: '⭐' },
};
const HOL_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const currentYear = new Date().getFullYear();
const weekday = (d) => new Date(d).toLocaleDateString('en-IN', { weekday: 'long' });
const daysLeft = (d) => {
  const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
  if (diff < 0) return null;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return `${diff} days`;
};
const INDIA_HOLIDAYS_2026 = [
  { name: "New Year's Day",     date: '2026-01-01', type: 'national' },
  { name: 'Makar Sankranti',    date: '2026-01-14', type: 'regional' },
  { name: 'Republic Day',       date: '2026-01-26', type: 'national' },
  { name: 'Holi',               date: '2026-03-04', type: 'national' },
  { name: 'Ram Navami',         date: '2026-03-28', type: 'national' },
  { name: 'Eid ul-Fitr',        date: '2026-03-31', type: 'national' },
  { name: 'Good Friday',        date: '2026-04-03', type: 'national' },
  { name: 'Ambedkar Jayanti',   date: '2026-04-14', type: 'national' },
  { name: 'Maharashtra Day',    date: '2026-05-01', type: 'regional' },
  { name: 'Eid ul-Adha',        date: '2026-06-07', type: 'national' },
  { name: 'Independence Day',   date: '2026-08-15', type: 'national' },
  { name: 'Janmashtami',        date: '2026-08-23', type: 'national' },
  { name: 'Gandhi Jayanti',     date: '2026-10-02', type: 'national' },
  { name: 'Dussehra',           date: '2026-10-21', type: 'national' },
  { name: 'Diwali',             date: '2026-11-08', type: 'national' },
  { name: 'Guru Nanak Jayanti', date: '2026-11-23', type: 'national' },
  { name: 'Christmas',          date: '2026-12-25', type: 'national' },
];

const STATUS_STYLE = {
  present:    { label: 'Present',    bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', dot: '#16a34a' },
  absent:     { label: 'Absent',     bg: '#fff1f2', color: '#e11d48', border: '#fecdd3', dot: '#e11d48' },
  half_day:   { label: 'Half Day',   bg: '#fffbeb', color: '#d97706', border: '#fde68a', dot: '#d97706' },
  on_leave:   { label: 'On Leave',   bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe', dot: '#7c3aed' },
  permission: { label: 'Permission', bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', dot: '#2563eb' },
  holiday:    { label: 'Holiday',    bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', dot: '#ea580c' },
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
};


/* ─── Admin View ─────────────────────────────────────────── */
function AdminAttendance() {
  const [activeTab, setActiveTab] = useState('attendance');
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [autoRunning, setAutoRunning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState(todayStr());
  const [form, setForm] = useState({
    user: '', project: '', attendanceDate: todayStr(),
    status: 'present', loginTime: '', logoutTime: '', remarks: ''
  });
  const [notifyUsers, setNotifyUsers] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [exportRange, setExportRange] = useState({ from: todayStr(), to: todayStr(), user: '' });
  const [exportRecords, setExportRecords] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // ── Holiday state ──
  const [holidays, setHolidays] = useState([]);
  const [holYear, setHolYear] = useState(currentYear);
  const [holSearch, setHolSearch] = useState('');
  const [holFilterType, setHolFilterType] = useState('');
  const [holFilterMonth, setHolFilterMonth] = useState('');
  const [holSeeding, setHolSeeding] = useState(false);
  const [fillingId, setFillingId] = useState(null);
  const [showHolModal, setShowHolModal] = useState(false);
  const [editingHol, setEditingHol] = useState(null);
  const [holForm, setHolForm] = useState({ name: '', date: '', type: 'national', description: '', isRecurring: false });
  const [holConfirm, setHolConfirm] = useState(null);

  const fetchHolidays = useCallback(() => {
    API.get('/holidays', { params: { year: holYear } }).then(r => setHolidays(r.data)).catch(() => {});
  }, [holYear]);
  useEffect(() => { if (activeTab === 'holidays') fetchHolidays(); }, [activeTab, fetchHolidays]);

  const openHolModal = (h = null) => {
    setEditingHol(h);
    setHolForm(h ? { name: h.name, date: h.date?.slice(0, 10), type: h.type, description: h.description || '', isRecurring: h.isRecurring || false }
                 : { name: '', date: '', type: 'national', description: '', isRecurring: false });
    setShowHolModal(true);
  };
  const handleHolSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingHol) { await API.put(`/holidays/${editingHol._id}`, holForm); toast.success('Holiday updated'); }
      else { await API.post('/holidays', holForm); toast.success('Holiday added'); }
      setShowHolModal(false); fetchHolidays();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };
  const handleHolDelete = async () => {
    const h = holConfirm.holiday; setHolConfirm(null);
    try { await API.delete(`/holidays/${h._id}`); toast.success('Deleted'); fetchHolidays(); }
    catch { toast.error('Error deleting'); }
  };
  const handleFillHoliday = (h) => setHolConfirm({ type: 'fill', holiday: h });
  const doFillHoliday = async () => {
    const h = holConfirm.holiday; setHolConfirm(null);
    setFillingId(h._id);
    try {
      const res = await API.post('/attendance/fill-holiday', { date: h.date, holidayName: h.name });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setFillingId(null); }
  };
  const handleSeedIndia = async () => {
    setHolSeeding(true);
    try {
      const list = INDIA_HOLIDAYS_2026.map(h => ({ ...h, date: h.date.replace('2026', String(holYear)) }));
      await Promise.all(list.map(h => API.post('/holidays', h).catch(() => {})));
      toast.success(`Indian holidays for ${holYear} added!`); fetchHolidays();
    } catch { toast.error('Error seeding'); } finally { setHolSeeding(false); }
  };
  const holFiltered = holidays.filter(h => {
    const matchSearch = !holSearch || h.name.toLowerCase().includes(holSearch.toLowerCase());
    const matchType   = !holFilterType  || h.type === holFilterType;
    const matchMonth  = !holFilterMonth || new Date(h.date).getMonth() === Number(holFilterMonth);
    return matchSearch && matchType && matchMonth;
  });
  const holByMonth = HOL_MONTHS.reduce((acc, m, i) => {
    const items = holFiltered.filter(h => new Date(h.date).getMonth() === i);
    if (items.length) acc[i] = items;
    return acc;
  }, {});
  const holUpcoming = holidays.filter(h => new Date(h.date) >= new Date()).length;
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewRecord, setViewRecord] = useState(null);

  const fetchRecords = useCallback(() => {
    const params = {};
    if (filterDate) { params.from = filterDate; params.to = filterDate; }
    API.get('/attendance', { params }).then(r => setRecords(r.data)).catch(() => {});
  }, [filterDate]);

  useEffect(() => {
    Promise.all([
      API.get('/users').then(r => setUsers(r.data.filter(u => !u.role?.isAdmin))).catch(() => {}),
      API.get('/projects').then(r => setProjects(r.data)).catch(() => {}),
      API.get('/attendance').then(r => setRecords(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const openModal = (record = null) => {
    setEditing(record);
    if (record) {
      setForm({
        user: record.user?._id || '',
        project: record.project?._id || '',
        attendanceDate: record.attendanceDate?.slice(0, 10) || todayStr(),
        status: record.status,
        loginTime: record.loginTime ? new Date(record.loginTime).toTimeString().slice(0, 5) : '',
        logoutTime: record.logoutTime ? new Date(record.logoutTime).toTimeString().slice(0, 5) : '',
        remarks: record.remarks || '',
      });
    } else {
      setForm({ user: '', project: '', attendanceDate: filterDate || todayStr(), status: 'present', loginTime: '', logoutTime: '', remarks: '' });
    }
    setNotifyUsers([]);
    setShowModal(true);
  };

  const buildPayload = () => {
    const payload = { ...form, notifyUsers };
    if (form.loginTime) payload.loginTime = new Date(`${form.attendanceDate}T${form.loginTime}:00`);
    else delete payload.loginTime;
    if (form.logoutTime) payload.logoutTime = new Date(`${form.attendanceDate}T${form.logoutTime}:00`);
    else delete payload.logoutTime;
    if (!payload.project) delete payload.project;
    return payload;
  };

  const toggleNotifyUser = (id) => {
    setNotifyUsers(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await API.put(`/attendance/${editing._id}`, buildPayload()); toast.success('Attendance updated'); }
      else { await API.post('/attendance', buildPayload()); toast.success('Attendance marked'); }
      setShowModal(false); fetchRecords();
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving attendance'); }
  };

  const handleDelete = (id) => setConfirm({ type: 'delete', id });
  const doDelete = async () => { // eslint-disable-line no-unused-vars
    const { id } = confirm; setConfirm(null);
    try { await API.delete(`/attendance/${id}`); toast.success('Deleted'); fetchRecords(); }
    catch { toast.error('Error deleting'); }
  };
  const doAutoAbsent = async () => { // eslint-disable-line no-unused-vars
    setConfirm(null); setAutoRunning(true);
    try {
      await API.post('/attendance/run-auto-absent');
      toast.success('Auto-absent completed');
      fetchRecords();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setAutoRunning(false); }
  };

  const fetchExportRecords = async () => {
    setExportLoading(true);
    try {
      const params = { from: exportRange.from, to: exportRange.to };
      if (exportRange.user) params.user = exportRange.user;
      const res = await API.get('/attendance', { params });
      setExportRecords(res.data);
    } catch { toast.error('Failed to fetch records'); }
    finally { setExportLoading(false); }
  };

  const doExport = (type) => {
    const data = exportRecords || filtered;
    if (!data.length) { toast.error('No records to export'); return; }
    const dateLabel = exportRange.from === exportRange.to
      ? new Date(exportRange.from + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : `${new Date(exportRange.from + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${new Date(exportRange.to + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    const selectedUser = exportRange.user ? users.find(u => u._id === exportRange.user)?.name : null;
    const title = selectedUser ? `Attendance — ${selectedUser}` : 'Attendance Report';
    const subtitle = `Period: ${dateLabel}${selectedUser ? ` | Employee: ${selectedUser}` : ''}`;
    const filename = `${selectedUser || 'Attendance'}_${exportRange.from}_to_${exportRange.to}`;

    if (type === 'excel-overall') exportExcel(data, filename);
    else if (type === 'pdf-overall') exportPDF(data, title, subtitle);
    else if (type === 'excel-byuser') exportExcelByUser(data, filename);
    else if (type === 'pdf-byuser') exportPDFByUser(data, title, subtitle);
    setShowExport(false); setExportRecords(null);
  };

  const filtered = records.filter(r => {
    const matchStatus = !filterStatus || r.status === filterStatus;
    const matchSearch = !search ||
      r.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
      r.user?.email?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const statCounts = Object.keys(STATUS_STYLE).reduce((acc, s) => {
    acc[s] = records.filter(r => r.status === s).length;
    return acc;
  }, {});

  return (
    <div className="page">
      {loading && <PageLoader text="Loading attendance..." />}
      <div className="page-actions">
        {/* Tab switcher */}
        <div className="att-tabs">
          <button className={`att-tab ${activeTab === 'attendance' ? 'att-tab--active' : ''}`} onClick={() => setActiveTab('attendance')}>
            <FiCalendar size={13} /> Attendance
          </button>
          <button className={`att-tab ${activeTab === 'holidays' ? 'att-tab--active' : ''}`} onClick={() => { setActiveTab('holidays'); fetchHolidays(); }}>
            <FiSun size={13} /> Holidays
          </button>
        </div>
        {/* Action buttons — conditional by tab */}
        {activeTab === 'attendance' && (
          <div className="att-action-btns">
            <button className="btn-auto-absent" disabled={autoRunning} onClick={() => setConfirm({ type: 'auto-absent' })}>
              <FiZap size={14} /> {autoRunning ? 'Running...' : 'Auto Absent'}
            </button>
            <button className="btn-export-sm" onClick={() => { setExportRange({ from: filterDate || todayStr(), to: filterDate || todayStr(), user: '' }); setExportRecords(null); setShowExport(true); }}>
              <FiDownload size={14} /> Export
            </button>
            <button className="btn-primary-sm" onClick={() => openModal()}><FiPlus /> Mark Attendance</button>
          </div>
        )}
        {activeTab === 'holidays' && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="hol-seed-btn" onClick={handleSeedIndia} disabled={holSeeding}>
              🇮🇳 {holSeeding ? 'Adding...' : `India Holidays ${holYear}`}
            </button>
            <button className="btn-primary-sm" onClick={() => openHolModal()}><FiPlus /> Add Holiday</button>
          </div>
        )}
      </div>

      {/* ─── Holidays Tab ─────────────────────────────────────── */}
      {activeTab === 'holidays' && (
        <div>
          {/* Stats */}
          <div className="hol-stats">
            <div className="hol-stat-card" style={{ borderTop: '3px solid #3b82f6' }}>
              <p className="hol-stat-num" style={{ color: '#3b82f6' }}>{holidays.length}</p>
              <p className="hol-stat-label">Total Holidays</p>
            </div>
            <div className="hol-stat-card" style={{ borderTop: '3px solid #16a34a' }}>
              <p className="hol-stat-num" style={{ color: '#16a34a' }}>{holUpcoming}</p>
              <p className="hol-stat-label">Upcoming</p>
            </div>
            <div className="hol-stat-card" style={{ borderTop: '3px solid #94a3b8' }}>
              <p className="hol-stat-num" style={{ color: '#94a3b8' }}>{holidays.length - holUpcoming}</p>
              <p className="hol-stat-label">Past</p>
            </div>
            {Object.entries(TYPE_STYLE).map(([key, st]) => (
              <div key={key} className="hol-stat-card" style={{ borderTop: `3px solid ${st.color}` }}>
                <p className="hol-stat-num" style={{ color: st.color }}>{holidays.filter(h => h.type === key).length}</p>
                <p className="hol-stat-label">{st.emoji} {st.label}</p>
              </div>
            ))}
          </div>
          {/* Filters */}
          <div className="hol-filters">
            <div className="search-box">
              <FiSearch />
              <input placeholder="Search holidays..." value={holSearch} onChange={e => setHolSearch(e.target.value)} />
            </div>
            <select className="al-select" value={holFilterType} onChange={e => setHolFilterType(e.target.value)}>
              <option value="">All Types</option>
              {Object.entries(TYPE_STYLE).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
            </select>
            <select className="al-select" value={holFilterMonth} onChange={e => setHolFilterMonth(e.target.value)}>
              <option value="">All Months</option>
              {HOL_MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <div className="hol-year-nav">
              <button onClick={() => setHolYear(y => y - 1)}>‹</button>
              <span>{holYear}</span>
              <button onClick={() => setHolYear(y => y + 1)}>›</button>
            </div>
            <button className="al-select" style={{ cursor: 'pointer' }} onClick={fetchHolidays}><FiRefreshCw size={13} /></button>
            <span className="result-count" style={{ marginLeft: 'auto' }}>{holFiltered.length} holiday{holFiltered.length !== 1 ? 's' : ''}</span>
          </div>
          {/* Calendar grouped view */}
          {Object.keys(holByMonth).length === 0 ? (
            <div className="hol-empty">
              <div style={{ fontSize: '3rem' }}>🗓️</div>
              <h3>No holidays found</h3>
              {holidays.length === 0 && (
                <p>Click <strong>"India Holidays"</strong> to quickly add all Indian public holidays for {holYear}.</p>
              )}
            </div>
          ) : (
            <div className="hol-months">
              {Object.entries(holByMonth).map(([monthIdx, items]) => (
                <div key={monthIdx} className="hol-month-block">
                  <div className="hol-month-header">
                    <FiCalendar size={14} />
                    <span>{HOL_MONTHS[monthIdx]} {holYear}</span>
                    <span className="hol-month-count">{items.length}</span>
                  </div>
                  <div className="hol-month-list">
                    {items.map(h => {
                      const st = TYPE_STYLE[h.type] || TYPE_STYLE.national;
                      const left = daysLeft(h.date);
                      const isPast = new Date(h.date) < new Date();
                      return (
                        <div key={h._id} className={`hol-item ${isPast ? 'past' : ''}`}>
                          <div className="hol-date-box" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                            <span className="hol-date-day">{new Date(h.date).getDate()}</span>
                            <span className="hol-date-month">{HOL_MONTHS[new Date(h.date).getMonth()]}</span>
                          </div>
                          <div className="hol-item-body">
                            <div className="hol-item-top">
                              <p className="hol-item-name">{h.name}</p>
                              <span className="hol-type-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                                {st.emoji} {st.label}
                              </span>
                            </div>
                            <div className="hol-item-meta">
                              <span>{weekday(h.date)}</span>
                              {h.isRecurring && <span className="hol-recurring">↺ Recurring</span>}
                              {h.description && <span className="hol-desc">{h.description}</span>}
                            </div>
                          </div>
                          <div className="hol-item-right">
                            {left && <span className={`hol-days-left ${left === 'Today' ? 'today' : ''}`}>{left}</span>}
                            <div className="hol-item-actions">
                              <button
                                className="hol-fill-btn"
                                onClick={() => handleFillHoliday(h)}
                                disabled={fillingId === h._id}
                                title="Fill holiday attendance for all employees"
                              >
                                <FiUsers size={13} />
                                {fillingId === h._id ? 'Filling...' : 'Fill Attendance'}
                              </button>
                              <div className="td-actions">
                                <button className="action-btn edit" onClick={() => openHolModal(h)}><FiEdit2 /></button>
                                <button className="action-btn delete" onClick={() => setHolConfirm({ type: 'delete', holiday: h })}><FiTrash2 /></button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Holiday Confirm Modals */}
          {holConfirm?.type === 'delete' && (
            <div className="modal-overlay" onClick={() => setHolConfirm(null)}>
              <div className="modal" style={{ maxWidth: 380, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🗑️</div>
                <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Delete Holiday</h3>
                <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>Delete "{holConfirm.holiday.name}"? This cannot be undone.</p>
                <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setHolConfirm(null)}>Cancel</button>
                  <button className="btn-danger" style={{ flex: 1 }} onClick={handleHolDelete}>Delete</button>
                </div>
              </div>
            </div>
          )}
          {holConfirm?.type === 'fill' && (
            <div className="modal-overlay" onClick={() => setHolConfirm(null)}>
              <div className="modal" style={{ maxWidth: 400, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>📋</div>
                <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Fill Holiday Attendance</h3>
                <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>
                  Mark all employees as <strong>Holiday</strong> for "{holConfirm.holiday.name}" ({new Date(holConfirm.holiday.date).toLocaleDateString('en-IN')})?
                </p>
                <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setHolConfirm(null)}>Cancel</button>
                  <button className="btn-primary" style={{ flex: 1 }} onClick={doFillHoliday}>Fill Attendance</button>
                </div>
              </div>
            </div>
          )}
          {showHolModal && (
            <div className="modal-overlay">
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingHol ? 'Edit Holiday' : 'Add Holiday'}</h2>
                  <button className="modal-close" onClick={() => setShowHolModal(false)}>✕</button>
                </div>
                <form onSubmit={handleHolSubmit}>
                  <div className="form-group">
                    <label>Holiday Name</label>
                    <input value={holForm.name} onChange={e => setHolForm({ ...holForm, name: e.target.value })} required placeholder="e.g. Diwali" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Date</label>
                      <input type="date" value={holForm.date} onChange={e => setHolForm({ ...holForm, date: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label>Type</label>
                      <select value={holForm.type} onChange={e => setHolForm({ ...holForm, type: e.target.value })}>
                        {Object.entries(TYPE_STYLE).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                    <input value={holForm.description} onChange={e => setHolForm({ ...holForm, description: e.target.value })} placeholder="Brief description..." />
                  </div>
                  <label className="hol-recurring-label">
                    <input type="checkbox" checked={holForm.isRecurring} onChange={e => setHolForm({ ...holForm, isRecurring: e.target.checked })} />
                    Recurring every year
                  </label>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={() => setShowHolModal(false)}>Cancel</button>
                    <button type="submit" className="btn-primary">{editingHol ? 'Update' : 'Add Holiday'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Attendance Tab ─────────────────────────────────────── */}
      {activeTab === 'attendance' && <>
      <div className="att-top-bar">
        <div className="att-date-pick">
          <FiCalendar />
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} />
        </div>
        <div className="att-stats">
          {Object.entries(STATUS_STYLE).map(([key, st]) => (
            <div key={key} className={`att-stat-chip ${filterStatus === key ? 'selected' : ''}`}
              style={{ '--chip-color': st.color, '--chip-bg': st.bg, '--chip-border': st.border }}
              onClick={() => setFilterStatus(filterStatus === key ? '' : key)}>
              <span className="att-stat-dot" style={{ background: st.dot }} />
              <span className="att-stat-num">{statCounts[key]}</span>
              <span className="att-stat-label">{st.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <div className="search-box">
            <FiSearch />
            <input placeholder="Search employee..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {filterStatus && (
            <button className="att-clear-filter" onClick={() => setFilterStatus('')}>
              <FiFilter size={12} /> {STATUS_STYLE[filterStatus].label} ✕
            </button>
          )}
          <span className="result-count">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="att-table-wrap">
        <table className="att-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Project</th>
              <th>Status</th>
              <th>Sessions</th>
              <th>Total Hours</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.present;
              return (
                <tr key={r._id}>
                  <td>
                    <div className="user-cell">
                      <div className="att-avatar">{r.user?.name?.[0]?.toUpperCase()}</div>
                      <div>
                        <p className="user-cell-name">{r.user?.name}</p>
                        <p className="user-cell-sub">{r.user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="text-muted">{r.project?.title || '—'}</td>
                  <td>
                    <span className="att-status-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                      <span className="att-stat-dot" style={{ background: st.dot }} />
                      {st.label}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                      {(r.sessions || []).length === 0 ? <span className="text-muted">—</span>
                        : (r.sessions || []).map((s, i) => (
                          <span key={i} style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                            <span style={{ background: '#f1f5f9', borderRadius: 4, padding: '1px 5px', fontSize: '0.68rem', fontWeight: 700 }}>#{i+1}</span>
                            {formatTime(s.clockIn)} → {s.clockOut ? formatTime(s.clockOut) : <span style={{ color: '#16a34a', fontWeight: 600 }}>Ongoing</span>}
                          </span>
                        ))}
                    </div>
                  </td>
                  <td><strong style={{ color: '#0f172a', fontSize: '0.875rem' }}>
                    {r.totalMinutes > 0 ? `${Math.floor(r.totalMinutes/60)}h ${Math.round(r.totalMinutes%60)}m` : '—'}
                  </strong></td>
                  <td className="text-muted" style={{ maxWidth: 140 }}>
                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.remarks || '—'}</span>
                  </td>
                  <td>
                    <div className="td-actions">
                      <button className="action-btn" onClick={() => setViewRecord(r)}><FiEye /></button>
                      <button className="action-btn edit" onClick={() => openModal(r)}><FiEdit2 /></button>
                      <button className="action-btn delete" onClick={() => handleDelete(r._id)}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="att-empty">No attendance records for this date.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Export Modal */}
      {showExport && (
        <div className="modal-overlay" onClick={() => { setShowExport(false); setExportRecords(null); }}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiDownload size={16} style={{ marginRight: 8 }} />Export Attendance</h2>
              <button className="modal-close" onClick={() => { setShowExport(false); setExportRecords(null); }}>✕</button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>From Date</label>
                <input type="date" value={exportRange.from} onChange={e => { setExportRange(r => ({ ...r, from: e.target.value })); setExportRecords(null); }} />
              </div>
              <div className="form-group">
                <label>To Date</label>
                <input type="date" value={exportRange.to} onChange={e => { setExportRange(r => ({ ...r, to: e.target.value })); setExportRecords(null); }} />
              </div>
            </div>

            <div className="form-group">
              <label>Employee <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional — leave blank for all)</span></label>
              <select value={exportRange.user} onChange={e => { setExportRange(r => ({ ...r, user: e.target.value })); setExportRecords(null); }}>
                <option value="">All Employees</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
            </div>

            <button
              className="att-export-fetch-btn"
              onClick={fetchExportRecords}
              disabled={exportLoading}
            >
              {exportLoading ? 'Fetching...' : exportRecords !== null ? `✓ ${exportRecords.length} records fetched — fetch again` : 'Fetch Records'}
            </button>

            {exportRecords !== null && (
              <div className="att-export-options">
                <p className="att-export-label">Overall Report ({exportRecords.length} records)</p>
                <div className="att-export-btns">
                  <button className="att-export-btn excel" onClick={() => doExport('excel-overall')}>
                    <FiGrid size={15} /> Excel
                  </button>
                  <button className="att-export-btn pdf" onClick={() => doExport('pdf-overall')}>
                    <FiFileText size={15} /> PDF
                  </button>
                </div>

                {!exportRange.user && (
                  <>
                    <p className="att-export-label" style={{ marginTop: '1rem' }}>Per Employee Report</p>
                    <div className="att-export-btns">
                      <button className="att-export-btn excel" onClick={() => doExport('excel-byuser')}>
                        <FiGrid size={15} /> Excel (sheets per employee)
                      </button>
                      <button className="att-export-btn pdf" onClick={() => doExport('pdf-byuser')}>
                        <FiFileText size={15} /> PDF (page per employee)
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {viewRecord && (
        <div className="modal-overlay" onClick={() => setViewRecord(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Attendance Detail</h2>
              <button className="modal-close" onClick={() => setViewRecord(null)}>✕</button>
            </div>
            <div style={{ padding: '0 0.25rem' }}>
              <div className="user-cell" style={{ marginBottom: '1rem' }}>
                <div className="att-avatar">{viewRecord.user?.name?.[0]?.toUpperCase()}</div>
                <div>
                  <p className="user-cell-name">{viewRecord.user?.name}</p>
                  <p className="user-cell-sub">{viewRecord.user?.email}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', fontSize: '0.82rem' }}>
                <div><span style={{ color: '#94a3b8' }}>Project</span><br/><strong>{viewRecord.project?.title || '—'}</strong></div>
                <div><span style={{ color: '#94a3b8' }}>Date</span><br/><strong>{new Date(viewRecord.attendanceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></div>
                <div><span style={{ color: '#94a3b8' }}>Total Hours</span><br/><strong>{viewRecord.totalMinutes > 0 ? `${Math.floor(viewRecord.totalMinutes/60)}h ${Math.round(viewRecord.totalMinutes%60)}m` : '—'}</strong></div>
              </div>
              <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.5rem' }}>Sessions</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '45vh', overflowY: 'auto' }}>
                {(viewRecord.sessions || []).length === 0 && <p className="text-muted">No sessions recorded</p>}
                {(viewRecord.sessions || []).map((s, i) => (
                  <div key={s._id || i} style={{ border: '1px solid #f1f5f9', borderRadius: 8, padding: '0.7rem 0.85rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', color: '#334155' }}>
                      <span style={{ background: '#f1f5f9', borderRadius: 4, padding: '1px 6px', fontSize: '0.68rem', fontWeight: 700 }}>#{i + 1}</span>
                      <FiLogIn size={12} /> {formatTime(s.clockIn)}
                      <span style={{ color: '#94a3b8' }}>→</span>
                      <FiLogOut size={12} /> {s.clockOut ? formatTime(s.clockOut) : <span style={{ color: '#16a34a', fontWeight: 600 }}>Ongoing</span>}
                    </div>
                    {s.note && <p style={{ fontSize: '0.76rem', color: '#64748b', fontStyle: 'italic', margin: '0.35rem 0 0' }}>{s.note}</p>}
                    {s.clockInPhoto && (
                      <a href={`${SERVER_ORIGIN}${s.clockInPhoto}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '0.5rem' }}>
                        <img src={`${SERVER_ORIGIN}${s.clockInPhoto}`} alt="Site" style={{ maxHeight: 140, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
              {viewRecord.remarks && (
                <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.9rem' }}><strong>Remarks:</strong> {viewRecord.remarks}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Attendance' : 'Mark Attendance'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Employee</label>
                  <select value={form.user} onChange={e => setForm({ ...form, user: e.target.value })} required>
                    <option value="">Select employee</option>
                    {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={form.attendanceDate} onChange={e => setForm({ ...form, attendanceDate: e.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="half_day">Half Day</option>
                    <option value="on_leave">On Leave</option>
                    <option value="permission">Permission</option>
                    <option value="holiday">Holiday</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Project</label>
                  <select value={form.project} onChange={e => setForm({ ...form, project: e.target.value })}>
                    <option value="">None</option>
                    {projects.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Login Time</label>
                  <input type="time" value={form.loginTime} onChange={e => setForm({ ...form, loginTime: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Logout Time</label>
                  <input type="time" value={form.logoutTime} onChange={e => setForm({ ...form, logoutTime: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2} placeholder="Optional notes..." />
              </div>

              {/* Notify staff */}
              {users.filter(u => u._id !== form.user).length > 0 && (
                <div className="form-group">
                  <label>Notify Staff <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                  <div className="notif-staff-list">
                    {users.filter(u => u._id !== form.user).map(u => (
                      <label key={u._id} className={`notif-staff-chip ${notifyUsers.includes(u._id) ? 'checked' : ''}`}>
                        <input type="checkbox" checked={notifyUsers.includes(u._id)} onChange={() => toggleNotifyUser(u._id)} style={{ display: 'none' }} />
                        <span className="notif-staff-avatar">{u.name?.[0]?.toUpperCase()}</span>
                        {u.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">{editing ? 'Update' : 'Mark Attendance'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirm?.type === 'delete' && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal" style={{ maxWidth: 380, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🗑️</div>
            <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Delete Attendance Record</h3>
            <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>Delete this attendance record? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="btn-danger" style={{ flex: 1 }} onClick={doDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {confirm?.type === 'auto-absent' && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal" style={{ maxWidth: 420, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>⚡</div>
            <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Auto-Mark Absent</h3>
            <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>Auto-mark absent for all employees who have not checked in today?</p>
            <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="btn-primary" style={{ flex: 1 }} onClick={doAutoAbsent}>Mark Absent</button>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

/* ─── User View ──────────────────────────────────────────── */
function UserAttendance({ currentUser }) {
  const [todayRecord, setTodayRecord] = useState(undefined);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteModal, setNoteModal] = useState(null); // 'in' | 'out'
  const [note, setNote] = useState('');
  const [showHolidayWarn, setShowHolidayWarn] = useState(false);
  const [clockInPhoto, setClockInPhoto] = useState(null);
  const requirePhoto = !!currentUser?.role?.requireClockInPhoto;

  const fetchToday = useCallback(() => {
    API.get('/attendance/today/me').then(r => setTodayRecord(r.data)).catch(() => setTodayRecord(null));
  }, []);

  const fetchHistory = useCallback(() => {
    API.get('/attendance/my/history').then(r => setHistory(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchToday(); fetchHistory(); }, [fetchToday, fetchHistory]);

  // Derived state from sessions
  const sessions = todayRecord?.sessions || [];
  const lastSession = sessions[sessions.length - 1];
  const isCurrentlyIn = lastSession && !lastSession.clockOut;
  const hasAnySession = sessions.length > 0;
  const totalMins = todayRecord?.totalMinutes || 0;
  const totalHrs = totalMins > 0 ? `${Math.floor(totalMins / 60)}h ${Math.round(totalMins % 60)}m` : '0h';
  const permMins = todayRecord?.permissionMinutes || 0;

  const isHolidayToday = todayRecord?.status === 'holiday';

  const handleClockIn = async () => {
    if (requirePhoto && !clockInPhoto) {
      toast.error('Please upload a site photo to clock in');
      return;
    }
    if (isHolidayToday && sessions.length === 0) {
      setShowHolidayWarn(true); return;
    }
    await doClockIn();
  };
  const doClockIn = async () => {
    setShowHolidayWarn(false);
    setLoading(true);
    try {
      let payload = { note };
      if (clockInPhoto) {
        payload = new FormData();
        payload.append('note', note);
        payload.append('photo', clockInPhoto);
      }
      await API.post('/attendance/clock-in', payload);
      toast.success(sessions.length === 0 ? 'Clocked in! Have a great day.' : 'Welcome back! Resumed work.');
      setNoteModal(null); setNote(''); setClockInPhoto(null);
      fetchToday(); fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error clocking in');
    } finally { setLoading(false); }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      await API.post('/attendance/clock-out', { note });
      toast.success('Clocked out successfully!');
      setNoteModal(null); setNote('');
      fetchToday(); fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error clocking out');
    } finally { setLoading(false); }
  };

  const st = STATUS_STYLE[todayRecord?.status] || STATUS_STYLE.present;

  return (
    <div className="page">

      {/* Holiday banner */}
      {isHolidayToday && !hasAnySession && (
        <div className="att-holiday-banner">
          <span className="att-holiday-banner-icon">🎉</span>
          <div>
            <p className="att-holiday-banner-title">Today is a Holiday</p>
            <p className="att-holiday-banner-sub">{todayRecord?.remarks?.replace('Holiday: ', '') || 'Public Holiday'} — Enjoy your day off! You can still clock in if you choose to work.</p>
          </div>
        </div>
      )}
      {isHolidayToday && hasAnySession && (
        <div className="att-holiday-banner working">
          <span className="att-holiday-banner-icon">💼</span>
          <div>
            <p className="att-holiday-banner-title">Working on Holiday</p>
            <p className="att-holiday-banner-sub">{todayRecord?.remarks?.split('|')[0]?.replace('Holiday: ', '').trim()} — Thank you for your dedication!</p>
          </div>
        </div>
      )}

      {/* Today card */}
      <div className="att-user-card">
        <div className="att-user-card-left">
          <div className="att-user-avatar">{currentUser?.name?.[0]?.toUpperCase()}</div>
          <div>
            <p className="att-user-name">{currentUser?.name}</p>
            <p className="att-user-role">{currentUser?.role?.name || 'Employee'}</p>
          </div>
        </div>

        <div className="att-today-info">
          {todayRecord === undefined ? (
            <p className="att-loading">Loading...</p>
          ) : !hasAnySession ? (
            <div className="att-not-clocked">
              <p className="att-status-text">Not clocked in today</p>
              <p className="att-status-sub">Click Clock In to start your day</p>
            </div>
          ) : (
            <div className="att-clocked-info">
              <div className="att-time-block">
                <span className="att-time-label">First In</span>
                <span className="att-time-value">{formatTime(sessions[0]?.clockIn)}</span>
              </div>
              <div className="att-time-divider">→</div>
              <div className="att-time-block">
                <span className="att-time-label">Last Out</span>
                <span className="att-time-value">{formatTime(lastSession?.clockOut)}</span>
              </div>
              <div className="att-time-block">
                <span className="att-time-label">Total</span>
                <span className="att-time-value" style={{ color: '#16a34a' }}>{totalHrs}</span>
              </div>
              {permMins > 0 && (
                <div className="att-time-block">
                  <span className="att-time-label">Break</span>
                  <span className="att-time-value" style={{ color: '#d97706' }}>{Math.round(permMins)}m</span>
                </div>
              )}
              {todayRecord?.status && (
                <span className="att-status-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}`, alignSelf: 'center' }}>
                  <span className="att-stat-dot" style={{ background: st.dot }} />{st.label}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="att-action-area">
          {todayRecord === undefined ? null : !isCurrentlyIn ? (
            <button className="att-clock-btn clock-in" onClick={() => setNoteModal('in')} disabled={loading}>
              <FiLogIn /> {sessions.length > 0 ? 'Resume Work' : 'Clock In'}
            </button>
          ) : (
            <button className="att-clock-btn clock-out" onClick={() => setNoteModal('out')} disabled={loading}>
              <FiLogOut /> Clock Out
            </button>
          )}
        </div>
      </div>

      {/* Sessions timeline for today */}
      {hasAnySession && (
        <div className="att-sessions-wrap">
          <h3 className="att-sessions-title">Today's Sessions</h3>
          <div className="att-sessions-timeline">
            {sessions.map((s, i) => (
              <div key={s._id || i} className="att-session-row">
                <div className={`att-session-dot ${!s.clockOut ? 'active' : ''}`} />
                <div className="att-session-body">
                  <div className="att-session-times">
                    <span className="att-session-in"><FiLogIn size={12} /> {formatTime(s.clockIn)}</span>
                    <span className="att-session-arrow">→</span>
                    <span className={`att-session-out ${!s.clockOut ? 'ongoing' : ''}`}>
                      <FiLogOut size={12} /> {s.clockOut ? formatTime(s.clockOut) : 'Ongoing...'}
                    </span>
                    {s.clockOut && (
                      <span className="att-session-dur">
                        {(() => { const m = (new Date(s.clockOut) - new Date(s.clockIn)) / 60000; return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`; })()}
                      </span>
                    )}
                  </div>
                  {s.note && <p className="att-session-note">{s.note}</p>}
                  {s.clockInPhoto && (
                    <a href={`${SERVER_ORIGIN}${s.clockInPhoto}`} target="_blank" rel="noopener noreferrer" className="att-session-photo-link">
                      📷 View site photo
                    </a>
                  )}
                </div>
                <span className="att-session-num">#{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Note modal for clock in/out */}
      {noteModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{noteModal === 'in' ? (sessions.length > 0 ? 'Resume Work' : 'Clock In') : 'Clock Out'}</h2>
              <button className="modal-close" onClick={() => { setNoteModal(null); setNote(''); setClockInPhoto(null); }}>✕</button>
            </div>
            <div className="form-group">
              <label>Note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <input
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={noteModal === 'out' ? 'e.g. Permission, Lunch break...' : 'e.g. Returned from site...'}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && (noteModal === 'in' ? handleClockIn() : handleClockOut())}
              />
            </div>
            {noteModal === 'in' && requirePhoto && (
              <div className="form-group">
                <label>Site Photo <span style={{ color: '#dc2626', fontWeight: 400 }}>(required)</span></label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={e => setClockInPhoto(e.target.files?.[0] || null)}
                />
                {clockInPhoto && (
                  <p style={{ fontSize: '.78rem', color: '#16a34a', marginTop: '.35rem' }}>✓ {clockInPhoto.name}</p>
                )}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => { setNoteModal(null); setNote(''); setClockInPhoto(null); }}>Cancel</button>
              <button
                className={`att-clock-btn ${noteModal === 'in' ? 'clock-in' : 'clock-out'}`}
                style={{ padding: '0.6rem 1.25rem', borderRadius: 8 }}
                onClick={noteModal === 'in' ? handleClockIn : handleClockOut}
                disabled={loading}
              >
                {noteModal === 'in' ? <FiLogIn /> : <FiLogOut />}
                {loading ? 'Please wait...' : noteModal === 'in' ? (sessions.length > 0 ? 'Resume' : 'Clock In') : 'Clock Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Attendance History</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="att-export-btn excel" style={{ flex: 'none', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
              onClick={() => { if (!history.length) { toast.error('No history to export'); return; } exportExcel(history, `My_Attendance_${currentUser?.name}`); }}>
              <FiGrid size={13} /> Excel
            </button>
            <button className="att-export-btn pdf" style={{ flex: 'none', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
              onClick={() => { if (!history.length) { toast.error('No history to export'); return; } exportPDF(history, `My Attendance — ${currentUser?.name}`, 'Last 30 records'); }}>
              <FiFileText size={13} /> PDF
            </button>
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Sessions</th>
                <th>Total Hours</th>
                <th>Break</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {history.map(r => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.present;
                const sess = r.sessions || [];
                const totalH = r.totalMinutes > 0 ? `${Math.floor(r.totalMinutes / 60)}h ${Math.round(r.totalMinutes % 60)}m` : '—';
                const breakM = r.permissionMinutes > 0 ? `${Math.round(r.permissionMinutes)}m` : '—';
                return (
                  <tr key={r._id}>
                    <td>
                      <p style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem', margin: 0 }}>
                        {new Date(r.attendanceDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      <p style={{ color: '#94a3b8', fontSize: '0.75rem', margin: 0 }}>
                        {new Date(r.attendanceDate).toLocaleDateString('en-IN', { weekday: 'long' })}
                      </p>
                    </td>
                    <td>
                      <span className="att-status-badge" style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                        <span className="att-stat-dot" style={{ background: st.dot }} />{st.label}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {sess.length === 0 ? <span className="text-muted">—</span> : sess.map((s, i) => (
                          <span key={i} style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                            <span style={{ background: '#f1f5f9', borderRadius: 4, padding: '1px 5px', fontSize: '0.68rem', fontWeight: 700, color: '#64748b' }}>#{i+1}</span>
                            {formatTime(s.clockIn)} → {s.clockOut ? formatTime(s.clockOut) : <span style={{ color: '#16a34a', fontWeight: 600 }}>Ongoing</span>}
                            {s.note && <span style={{ color: '#94a3b8' }}>({s.note})</span>}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td><strong style={{ color: '#0f172a' }}>{totalH}</strong></td>
                    <td><span style={{ color: '#d97706', fontWeight: 600, fontSize: '0.82rem' }}>{breakM}</span></td>
                    <td className="text-muted">{r.remarks || '—'}</td>
                  </tr>
                );
              })}
              {history.length === 0 && (
                <tr><td colSpan={6} className="att-empty">No attendance history yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showHolidayWarn && (
        <div className="modal-overlay" onClick={() => setShowHolidayWarn(false)}>
          <div className="modal" style={{ maxWidth: 400, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🎉</div>
            <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>Today is a Holiday!</h3>
            <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>Are you sure you want to clock in and work today?</p>
            <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
              <button className="btn-secondary" style={{ flex: 1 }} onClick={() => setShowHolidayWarn(false)}>No, take a break</button>
              <button className="btn-primary" style={{ flex: 1 }} onClick={doClockIn}>Yes, clock in</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main export ─────────────────────────────────────────── */
export default function Attendance() {
  const { user } = useAuth();
  return (
    <Layout>
      {user?.role?.isAdmin
        ? <AdminAttendance />
        : <UserAttendance currentUser={user} />
      }
    </Layout>
  );
}
